/* eslint-disable react/prop-types */
import React, { useCallback, useMemo, useState } from 'react'

import { Trans, useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'

import {
  IconAvatarDollar,
  IconImageOutline,
  IconLiveNotification,
  IconNotificationChatFill,
  IconNotificationPhotoFill,
  IconNotificationPlayFill,
  IconPrivateStream
} from 'assets/svg/sprite'
import { Button } from 'components/Button/Button'
import LikeHeart from 'components/UI/LikeHeart/LikeHeart'
import FriendRequestNotification from 'components/UI/Notifications/FriendRequest/FriendRequestNotification'
import SubscriptionExpiredNotifcation from 'components/UI/Notifications/SubscriptionExpired/SubscriptionExpiredNotification'
import SubscriptionExpiringNotifcation from 'components/UI/Notifications/SubscriptionExpiring/SubscriptionExpired/SubscriptionExpiringNotification'
import SubscriptionPriceChangedNotification from 'components/UI/Notifications/SubscriptionPriceChanged/SubscriptionPriceChangedNotification'
import TextWithIcon from 'components/UI/NotificationToast/components/TextWithIcon'
import { NotificationToast } from 'components/UI/NotificationToast/NotificationToast'
import { getLink } from 'helpers/appLinks'
import { toFixedNumber } from 'helpers/dataTransformations'
import { Icons } from 'helpers/icons'
import NotificationCardLayout from 'layouts/NotificationCardLayout/NotificationCardLayout'
import PostModal from 'pages/postModal'
import {
  ACCOUNT_COMPROMISED,
  ACCOUNT_COMPROMISE_2FA_REQUIRED,
  CARD_EXPIRING,
  MODEL_SUBSCRIPTION_EXPIRED,
  NEW_ACCEPTED_FRIEND_REQUEST,
  NEW_COMMENT_COMMENT,
  NEW_COMMENT_LIKE,
  NEW_INCOMING_FRIEND_REQUEST,
  NEW_MENTION,
  NEW_MESSAGE,
  NEW_MESSAGE_PURCHASE,
  NEW_PHOTO_COMMENT,
  NEW_PHOTO_LIKE,
  NEW_POST,
  NEW_POST_COMMENT,
  NEW_POST_LIKE,
  NEW_POST_TIP,
  NEW_POST_TIP_LIKE,
  NEW_PREMIUM_CONTENT_PURCHASE,
  NEW_PURCHASE_LIKE,
  NEW_REFERRAL,
  NEW_STORY_LIKE,
  NEW_STORY_TIP,
  NEW_SUBSCRIBER,
  NEW_TIP,
  NEW_TIP_LIKE,
  NEW_UPCOMING_STREAM_REMINDER,
  NEW_VIDEO_COMMENT,
  NEW_VIDEO_LIKE,
  PAYOUT_FAILED,
  PAYOUT_METHOD_REJECTED,
  POLL_RESPONSE,
  POST_FINISHED,
  POST_REMOVED,
  PRIVATE_STREAM_REQUEST,
  STREAM_STARTED,
  SUBSCRIPTION_EXPIRED,
  SUBSCRIPTION_EXPIRING,
  SUBSCRIPTION_PRICE_CHANGE,
  SUSPICIOUS_LOGIN,
  TICKET_RESPONSE
} from 'services/notifications/notificationTypes'
import { type INotificationCard } from 'types/interfaces/INotificationCard'

import styles from './_NotificationCard.module.scss'

const NotificationCard = ({
  data,
  type,
  time,
  isToast = false
}: INotificationCard) => {
  const { t } = useTranslation()
const navigate = useNavigate()
const [postModal, setPostModal] = useState<{
  postId: string
  commentId?: string
  post?: any
} | null>(null)

const openPost = useCallback((postInput: any, commentId?: string) => {
  const postId =
    typeof postInput === 'object'
      ? postInput?.id
      : postInput

  if (!postId) return

  setPostModal({
    postId,
    post:
      typeof postInput === 'object'
        ? postInput
        : undefined,
    commentId: commentId ?? undefined
  })
}, [])
  
  const closePost = useCallback(() => {
    setPostModal(null)
  }, [])

const PostLink = useCallback(
  ({
    postId,
    post,
    commentId,
    children,
  }: {
    postId: any
    post?: any
    commentId?: any
    children: React.ReactNode
  }) => (
    <span
      role="button"
      tabIndex={0}
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        openPost(post?.id ? post : postId, commentId)
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          e.stopPropagation()
          openPost(post?.id ? post : postId, commentId)
        }
      }}
      style={{ cursor: 'pointer' }}
    >
      {children}
    </span>
  ),
  [openPost]
)

  const specificNotification = (type: string, data: any) => {
    let newData = data

    const normalizedSender =
      data?.sender ?? data?.liker ?? data?.subscriber ?? data?.commenter

    newData = {
      ...data,
      sender: normalizedSender,
      isToast
    }

    const systemData = {
      ...newData,
      isSystem: true,
      sender: {
        avatar: '/notifier.svg',
        name: 'Notifier'
      }
    }

    let content = null
    let onBodyClick: (() => void) | undefined

    switch (type) {
      case PAYOUT_METHOD_REJECTED:
        newData = systemData
        content = (
          <div className="notificationCard__body__type">
            <div className={`${styles.contentRow}`}>
              <Trans i18nKey={'notifications:payoutMethodRejected'}>
                rejected
              </Trans>
              <Button
                text={<img src={Icons.chevronRight} alt={t('toggleArrow')} />}
                color={'grey'}
                customClass={styles.arrowRightBtn}
              />
            </div>
          </div>
        )
        break

      case PAYOUT_FAILED:
        newData = systemData

        content = (
          <div className="notificationCard__body__type">
            <span>
              Your payout of ${toFixedNumber(data?.payout?.amount ?? 0)} failed and has been refunded to your balance.
            </span>
          </div>
        )
        break

      case POST_REMOVED: {
        newData = systemData

        const post = data?.post
        const postId = data?.likeable?.id

        onBodyClick = () => openPost(post?.id ? post : postId)

        content = (
          <div className="notificationCard__body__type">
            <Trans i18nKey="notifications:postRemoved" />{' '}
          </div>
        )

        break
      }

case TICKET_RESPONSE:
  newData = systemData

  onBodyClick = () => {
    if (data?.ticket?.id) {
      navigate(`/support/tickets/${data.ticket.id}`)
    }
  }

  content = (
    <div className="notificationCard__body__type">
      <Trans i18nKey={'notifications:ticketResponse'}>
        ticketResponse
      </Trans>
    </div>
  )
  break
case CARD_EXPIRING:
  newData = systemData

  onBodyClick = () => {
    navigate('/settings/general/wallet')
  }

  content = (
    <div className="notificationCard__body__type">
<Trans
  i18nKey="notifications:cardExpiring"
  values={{
    last4: data?.last4
  }}
  components={{
    lastFour: <span className={styles.cardLastFour} />
  }}
/>
    </div>
  )
  break
  case ACCOUNT_COMPROMISED:
  newData = systemData

  content = (
    <div className="notificationCard__body__type">
      <span>
        {data?.message ??
          'We detected unusual login activity and temporarily restricted your account. Please check your email to secure your account.'}
      </span>
    </div>
  )
  break
case ACCOUNT_COMPROMISE_2FA_REQUIRED:
  newData = systemData

  onBodyClick = () => {
    const url =
      data?.action?.url ??
      '/settings/account/privacy-and-security/two-step-verification'

    navigate(url)
  }

  content = (
    <div className="notificationCard__body__type">
      <span>
        {data?.message ??
          'We detected additional unusual login activity and temporarily restricted your account. Set up two-factor authentication to restore access.'}
      </span>
    </div>
  )
  break
      case SUSPICIOUS_LOGIN:
        newData = systemData
        content = (
          <div className="notificationCard__body__type">
            <Trans i18nKey={'notifications:suspiciousLogin'}>
              Suspicious
              <Link to={`/security/suspicious-login/${data?.suspiciousLogin?.id}`}>
                attempt
              </Link>
              you?
            </Trans>
            <div className={`${styles.contentRow} ${styles.btnsRow}`}>
              <Button text={t('notifications:wasMe')} color={'blue'} padding="1-15" />
              <Button text={t('notifications:wasntMe')} color={'grey'} padding="1-15" />
            </div>
          </div>
        )
        break

      case POST_FINISHED: {
        newData = systemData

        const username = data?.sender?.username ?? newData?.sender?.username

        content = (
          <div className="notificationCard__body__type">
            <Trans i18nKey={'notifications:postFinishedProcessing'}>
              <Link to={username ? `/${username}` : '/'}>
                post
              </Link>
              postFinishedProcessing
            </Trans>
          </div>
        )
        break
      }

      case SUBSCRIPTION_PRICE_CHANGE:
        content = (
          <div className="notificationCard__body__type">
             <SubscriptionPriceChangedNotification data={newData} />
          </div>
        )
        break

      case MODEL_SUBSCRIPTION_EXPIRED:
        content = (
          <div className="notificationCard__body__type">
            <Trans i18nKey={'notifications:modelSubscriptionExpired'}>
              expired
            </Trans>
          </div>
        )
        break

      case SUBSCRIPTION_EXPIRED:
        content = (
          <div className="notificationCard__body__type">
            <SubscriptionExpiredNotifcation data={newData} />
          </div>
        )
        break

      case SUBSCRIPTION_EXPIRING:
        content = (
          <div className="notificationCard__body__type">
            <SubscriptionExpiringNotifcation data={newData} />
          </div>
        )
        break

      // --- Likes / comments that link to POSTS: swap to modal ---
      case NEW_VIDEO_LIKE: {
        const post = data?.post
        const postId = data?.likeable?.id
        onBodyClick = () => openPost(post?.id ? post : postId)
        content = (
          <div className="notificationCard__body__type">
            <span>{t('likedYour')} </span>
            <PostLink postId={postId} post={post}>{t('video')}</PostLink>
             <span className={styles.textIcon}>
              <IconNotificationPlayFill />
            </span>
          </div>
        )
        break
      }

       case NEW_PHOTO_LIKE: {
        const post = data?.post
        const postId = data?.likeable?.id
        onBodyClick = () => openPost(post?.id ? post : postId)

        content = (
          <div className="notificationCard__body__type">
            <span>{t('likedYour')} </span>
            <PostLink postId={postId} post={post}>{t('photo')}</PostLink>
{/*             <span className={styles.textIcon}>
              <IconNotificationPhotoFill />
            </span> */}
          </div>
        )
        break
      }
      case NEW_STORY_LIKE: {
        content = (
          <div className="notificationCard__body__type">
            <span>{t('likedYour')} </span>
            <span>{t('story')}</span>
          </div>
        )

        break
      }
      case NEW_POST_LIKE: {
        const post = data?.post
        const postId = data?.likeable?.id
        onBodyClick = () => openPost(post?.id ? post : postId)
        content = (
          <div className="notificationCard__body__type">
            <span>{t('likedYour')} </span>
            <PostLink postId={postId} post={post}>{t('postLC')}</PostLink>
          </div>
        )
        break
      }

      case NEW_POST: {
        const post = data?.post
        const postId = post?.id ?? post

        onBodyClick = () => openPost(post?.id ? post : postId)

        content = (
          <div className="notificationCard__body__type">
            <Trans i18nKey={'notifications:newPostText'}>
              NewPostText
              <PostLink postId={postId} post={post}>new post</PostLink>
            </Trans>
          </div>
        )
        break
      }

      case NEW_PREMIUM_CONTENT_PURCHASE: {
        const post = data?.post
        const postId = data?.post?.id
         onBodyClick = () => openPost(post?.id ? post : postId)
        content = (
          <div className={styles.contentRow}>
            <div className="notificationCard__body__type">
              <Trans
                i18nKey="notifications:purchasedPremiumContent"
                values={{ price: toFixedNumber(data?.post?.price) }}
              >
                Has purchased your
                <PostLink postId={postId} post={post}>
                  premium content for price: {toFixedNumber(data?.post?.price)}
                </PostLink>
              </Trans>
{/*               <span className={styles.textIcon}>
                <IconAvatarDollar />
              </span> */}
            </div>
            {!data?.sender?.blocked &&
              data?.can_like_notification === true && (
            <div
              className={`${styles.notificationActions} ${styles.likeAction}`}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
            >
            <LikeHeart
                isLiked={data?.is_liked}
                likeableType="notification"
                likeableId={data?.notification?.id}
                notificationType={type}
            />
            </div>
            )}
          </div>
        )
        break
      }

      case PRIVATE_STREAM_REQUEST:
        content = (
          <div className="notificationCard__body__type">
            <Trans i18nKey={'notifications:privateStreamRequest'}>
              has
              <PostLink postId={data?.post?.id}>private stream</PostLink>
            </Trans>
{/*             <span className={styles.textIcon}>
              <IconPrivateStream />
            </span> */}
          </div>
        )
        break

      case STREAM_STARTED: {
        const username = newData?.sender?.username

        content = (
          <div className="notificationCard__body__type">
            <Trans i18nKey={'notifications:hasStartedLiveStream'}>
              has
              <Link
                to={username ? `/watch/${username}` : '/watch'}
                state={{ data: newData?.sender }}
              >
                Live Stream
              </Link>
            </Trans>
{/*             <span className={styles.textIcon}>
              <IconLiveNotification />
            </span> */}
          </div>
        )
        break
      }

      case NEW_UPCOMING_STREAM_REMINDER:
        content = (
          <div className="notificationCard__body__type">
            <Trans i18nKey={'notifications:upcomingStreamReminder'}>
              have
              <PostLink postId={data?.post?.id}>upcoming stream</PostLink>
            </Trans>
{/*             <span className={styles.textIcon}>
              <IconPrivateStream />
            </span> */}
          </div>
        )
        break

      // --- Keep non-post links as-is ---
      case NEW_COMMENT_LIKE:
        content = (
          <div className="notificationCard__body__type">
            <TextWithIcon
              text={t('likedYour')}
              linkText={t('comment')}
              linkUrl={getLink.comment(data?.comment_post_id, data?.likeable?.id)}
              icon={IconNotificationChatFill}
            />
          </div>
        )
        break

      case NEW_TIP_LIKE:
        content = (
          <div className="notificationCard__body__type">
            <TextWithIcon
              text={t('likedYour')}
              linkText={t('tip')}
              linkUrl={getLink.sales()}
              // icon={IconAvatarDollar}
            />
          </div>
        )
        break

      case NEW_PURCHASE_LIKE:
        content = (
          <div className="notificationCard__body__type">
            <TextWithIcon
              text={t('likedYour')}
              linkText={t('purchase')}
              linkUrl={getLink.sales()}
              // icon={IconAvatarDollar}
            />
          </div>
        )
        break

      case NEW_POST_TIP_LIKE:
        content = (
          <div className="notificationCard__body__type">
            <TextWithIcon
              text={t('likedYour')}
              linkText={t('tip')}
              linkUrl={getLink.sales()}
              // icon={IconAvatarDollar}
            />
          </div>
        )
        break
      case NEW_STORY_TIP: {
        content = (
          <div className={styles.contentRow}>
            <div className="notificationCard__body__type">
              <span>
                {t('notifications:hasTippedYour')}{' '}
              </span>

              <span className={styles.contentText}>
                {'story'}
              </span>

              <span>
                {`$${toFixedNumber(data?.tip?.price ?? 0)}`}
              </span>

              {data?.tip?.message && (
                <p className={styles.message}>
                  &quot;{data.tip.message}&quot;
                </p>
              )}
            </div>

            {!data?.sender?.blocked &&
              data?.can_like_notification === true && (
                <div
                  className={`${styles.notificationActions} ${styles.likeAction}`}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  onPointerDown={(e) => e.stopPropagation()}
                  onTouchStart={(e) => e.stopPropagation()}
                >
                  <LikeHeart
                    isLiked={data?.is_liked}
                    likeableType="notification"
                    likeableId={data?.notification?.id}
                    notificationType={type}
                  />
                </div>
              )}
          </div>
        )

        break
      }
      case NEW_MESSAGE: {
        const fromUserId = data?.from_user_id ?? newData?.sender?.id
        const body = data?.body ?? data?.message?.body ?? ''
        const hasAttachments =
          Boolean(data?.has_attachments) ||
          Boolean(
            (data?.message?.photos?.length ?? 0) > 0 ||
              (data?.message?.videos?.length ?? 0) > 0 ||
              (data?.message?.videos_preview?.length ?? 0) > 0 ||
              (data?.message?.sounds?.length ?? 0) > 0
          )

        content = (
          <div className={styles.contentRow}>
            <div className="notificationCard__body__type">
              <Link to={fromUserId ? `/chat/${fromUserId}` : '/chat'}>
                {body}
                {body === '' && hasAttachments && (
                  <span className={styles.chatMediaNotificationText}>
                    {t('notifications:sentMessage')}
                    <IconImageOutline width="15" height="15" color="#2F98FE" />
                  </span>
                )}
              </Link>
            </div>
          </div>
        )
        break
      }
      case POLL_RESPONSE:
        content = (
          <div className={styles.contentRow}>
            <div className="notificationCard__body__type">
              <span>responded to your poll</span>
            </div>
          </div>
        )
        break
      case NEW_MESSAGE_PURCHASE:
        content = (
          <div className={styles.contentRow}>
            <div className="notificationCard__body__type">
            <Trans
              i18nKey="notifications:purchasedMessage"
              values={{
                price: toFixedNumber(data?.price || 0),
              }}
            >
              purchased
              <Link to={getLink.sales()} />
            </Trans>
            </div>
            {!data?.sender?.blocked &&
               data?.can_like_notification === true && (
            <div
              className={`${styles.notificationActions} ${styles.likeAction}`}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
            >
            <LikeHeart
                isLiked={data?.is_liked}
                likeableType="notification"
                likeableId={data?.notification?.id}
                notificationType={type}
            />
            </div>
            )}
          </div>
        )
        break

      case NEW_TIP:
        content = (
          <div className={styles.contentRow}>
            <div className="notificationCard__body__type">
              <span className={styles.contentText}>
                {t('notifications:hasTippedYouFor')}
              </span>
              <Link to={getLink.sales()}>{`$${toFixedNumber(data?.tip?.price)}`}</Link>
              {data?.tip?.message && (
                <p className={styles.message}>&quot;{data?.tip?.message}&quot;</p>
              )}
{/*               <span className={styles.textIcon}>
                <IconAvatarDollar />
              </span> */}
            </div>
            {!data?.sender?.blocked &&
              data?.can_like_notification === true && (
            <div
              className={`${styles.notificationActions} ${styles.likeAction}`}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
            >
            <LikeHeart
                isLiked={data?.is_liked}
                likeableType="notification"
                likeableId={data?.notification?.id}
                notificationType={type}
            />
            </div>
            )}
          </div>
        )
        break

      case NEW_POST_TIP: {
        const postId = data?.post ?? data?.post?.id
        const post = data?.post
          onBodyClick = () => openPost(post?.id ? post : postId)

        content = (
          <div className={styles.contentRow}>
            <div className="notificationCard__body__type">
            <span>
              {t('notifications:hasTippedYour')}{' '}
            </span>
            <span className={styles.contentText}>
            <PostLink postId={postId} post={post}>{t('postLC')}</PostLink>
            </span>
              <span>{`$${toFixedNumber(data?.tip?.price)}`}</span>

              {data?.tip?.message && (
                <p className={styles.message}>&quot;{data?.tip?.message}&quot;</p>
              )}

{/*               <span className={styles.textIcon}>
                <IconAvatarDollar />
              </span> */}
            </div>
            {!data?.sender?.blocked &&
              data?.can_like_notification === true && (
            <div
              className={`${styles.notificationActions} ${styles.likeAction}`}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
            >
            <LikeHeart
                isLiked={data?.is_liked}
                likeableType="notification"
                likeableId={data?.notification?.id}
                notificationType={type}
            />
            </div>
            )}
          </div>
        )
        break
      }

      case NEW_INCOMING_FRIEND_REQUEST:
        content = (
          <div className="notificationCard__body__type">
            <FriendRequestNotification data={newData} />
          </div>
        )
        break

      case NEW_POST_COMMENT: {
        const post = data?.post
        const postId = data?.commentable?.id
        const commentId = data?.comment?.id
        onBodyClick = () => openPost(post?.id ? post : postId, commentId)
        content = (
          <div className="notificationCard__body__type">
            <PostLink postId={postId} post={post} commentId={commentId}>{t('commented')}</PostLink>
            <span> {t('on')} {t('postLC')}</span>
{/*             <span className={styles.textIcon}>
              <IconNotificationChatFill />
            </span> */}
          </div>
        )
        break
      }

      case NEW_VIDEO_COMMENT: {
        const post = data?.post
        const postId = data?.commentable?.id
        const commentId = data?.comment?.id
        onBodyClick = () => openPost(post?.id ? post : postId, commentId)
        content = (
          <div className="notificationCard__body__type">
            <PostLink postId={postId} post={post} commentId={commentId}>{t('commented')}</PostLink>
            <span> {t('on')} {t('postLC')}</span>
{/*              <span className={styles.textIcon}>
              <IconNotificationChatFill />
            </span>  */}
          </div>
        )
        break
      }

      case NEW_PHOTO_COMMENT: {
        const post = data?.post
        const postId = data?.commentable?.id
        const commentId = data?.comment?.id
        onBodyClick = () => openPost(post?.id ? post : postId, commentId)
        content = (
          <div className="notificationCard__body__type">
            <PostLink postId={postId} post={post} commentId={commentId}>{t('commented')}</PostLink>
            <span> {t('on your')} {t('postLC')}</span>
{/*             <span className={styles.textIcon}>
              <IconNotificationChatFill />
            </span> */}
          </div>
        )
        break
      }

      case NEW_COMMENT_COMMENT: {
        const post = data?.post
        const postId = data?.commentable?.id
        const commentId = data?.comment?.id
        onBodyClick = () => openPost(post?.id ? post : postId, commentId)
        content = (
          <div className="notificationCard__body__type">
            <span>{t('hasReplied')} </span>
            <PostLink postId={postId} post={post} commentId={commentId}>{t('comment')}</PostLink>
{/*             <span className={styles.textIcon}>
              <IconNotificationChatFill />
            </span> */}
          </div>
        )
        break
      }

      case NEW_MENTION:
        content = (
          <div className="notificationCard__body__type">
            {data?.mentionable?.type === 'App\\Models\\Post' ? (
              <Trans i18nKey={'notifications:mentionInPost'}>
                Mentioned in their{' '}
                <PostLink postId={data?.mentionable?.id}>post.</PostLink>
              </Trans>
            ) : (
              <Trans i18nKey={'notifications:mentionInComment'}>
                Mentioned in their{' '}
                <Link
                  to={getLink.comment(data?.comment_post_id, data?.mentionable?.id)}
                >
                  comment.
                </Link>
              </Trans>
            )}
{/*             <span className={styles.textIcon}>
              <IconNotificationChatFill />
            </span> */}
          </div>
        )
        break

      case NEW_SUBSCRIBER:
        // keep your existing rendering
        content = (
          <div className="notificationCard__body__type">
            <Trans i18nKey={'notifications:subscriptionBundle'}>subscribed</Trans>
          </div>
        )
        break

      case NEW_REFERRAL:
        content = (
          <div className="notificationCard__body__type">
            <Trans i18nKey={'notifications:newReferralText'}>
              have
              <Link to={getLink.profile(newData?.sender?.id)}>referral</Link>
            </Trans>
          </div>
        )
        break

      case NEW_ACCEPTED_FRIEND_REQUEST:
        content = (
          <div className="notificationCard__body__type">
            <Trans i18nKey={'notifications:hasAcceptedFriendRequest'}>
              accepted friend request
            </Trans>
          </div>
        )
        break
    }

    return content
      ? {
          data: newData,
          content,
          onBodyClick 
        }
      : null
  }

  const specific = useMemo(() => specificNotification(type, data), [type, data])

const postThumbnail =
  specific?.data?.post?.thumbnail ??
  specific?.data?.post?.thumb_url ??
  specific?.data?.post?.thumb ??
  specific?.data?.post?.photos?.[0]?.thumb_url ??
  specific?.data?.post?.photos?.[0]?.url ??
  specific?.data?.post?.photos_preview?.[0]?.thumb_url ??
  specific?.data?.post?.photos_preview?.[0]?.url ??
  specific?.data?.post?.videos?.[0]?.thumb_url ??
  specific?.data?.post?.videos?.[0]?.thumbnail_src ??
  specific?.data?.post?.videos_preview?.[0]?.thumb_url ??
  specific?.data?.post?.videos_preview?.[0]?.url ??
  null

if (!specific) return null

/*
 * The realtime event still arrives so global notification
 * state/unread counters can update, but don't render the
 * popup when toast notifications are disabled.
 */
if (
  isToast &&
  data?.show_toast === false
) {
  return null
}


  const avatarSrc =
    typeof specific?.data?.sender?.avatar === 'string'
      ? specific?.data?.sender?.avatar
      : specific?.data?.sender?.avatar?.url

  return (
    <>
      {postModal && (
        <PostModal
          postId={postModal.postId}
          post={postModal.post}
          commentId={postModal.commentId}
          closeFn={closePost}
        />
      )}

      {isToast ? (
        <NotificationToast
          notificationId={specific?.data?.notification?.id}
          avatarSrc={avatarSrc}
          isOnline={specific?.data?.is_online}
          hasDollar={specific?.data?.sender?.dollar}
          name={specific?.data?.sender?.name}
          atName={specific?.data?.sender?.username}
          profileUrl={
            specific?.data?.sender?.id
              ? getLink.profile(specific.data.sender.id)
              : undefined
          }
          isSystem={specific?.data?.isSystem}
          time={time}
        >
          {specific.content}
        </NotificationToast>
      ) : (
        <NotificationCardLayout
          id={specific?.data?.sender?.id}
          onBodyClick={(specific as any)?.onBodyClick}
          avatarSrc={avatarSrc}
          postThumbnail={postThumbnail}
          isOnline={specific?.data?.is_online}
          hasDollar={specific?.data?.sender?.dollar}
          name={specific?.data?.sender?.name}
          showFanOptions={specific?.data?.show_fan_options}
          atName={specific?.data?.sender?.username}
          profileUrl={
            specific?.data?.sender?.id
              ? getLink.profile(specific.data.sender.id)
              : undefined
          }
          time={time}
          isSystem={specific?.data?.isSystem}
        >
          {specific.content}
        </NotificationCardLayout>
      )}
    </>
  )
}

export default NotificationCard
